import { useState, useEffect, useRef, useContext } from 'react'
import { getMessaging, onMessage } from 'firebase/messaging'
import axios from 'axios'
import dayjs from 'dayjs'
import { withRouter, Loading, getUserToShow, formattedDate, formattedTime, useTitle } from '../shared'
import { Layout , AlertContext, Button } from '../widgets'
import { PaginatedList } from '../components'
import * as routes from '../routes'
import Cell from '../components/Cell'
import authClient from '../auth/authClient'
import sendMessage from '../images/message-send.svg'
import sadBoi from '../images/sad-boi.svg'

const ConversationView = (props) => {
  const conversationId = props.router.params.id

  const listRef = useRef(null)
  const textareaRef = useRef(null)
  const textareaContainerRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [displayUser, setDisplayUser] = useState(null)
  const [conversationMembers, setConversationMembers] = useState([])
  const [messages, setMessages] = useState([])
  const [newestMessage, setNewestMessage] = useState(null)
  const [lastReadMessage, setLastReadMessage] = useState(null)
  const [newMessage, setNewMessage] = useState('')
  const [lastRefreshTimestamp, setLastRefreshTimestamp] = useState(Date.now())
  const [isConversationBlocked, setIsConversationBlocked] = useState(false)
  const [isUserBlocked, setIsUserBlocked] = useState(false)

  const { handleError, showAlert, showAlertConfirm } = useContext(AlertContext)

  const currentUser = authClient.currentUser()

  useTitle('Messages')

  useEffect(() => {
    // when a browser notification is clicked
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [newestMessage])

  useEffect(() => {
    axios.get(routes.dynamic(routes.conversationUrl, conversationId)).then((response) => {
      const conversation = response.data
      setConversationMembers(conversation.members)
      setDisplayUser(getUserToShow(conversation))
      setNewestMessage(conversation.newest_message)
      setIsConversationBlocked(conversation.is_blocked)

      setLoading(false)
    }).catch(handleError)
  }, [conversationId])

  useEffect(() => {
    if (!displayUser || displayUser.user_id === currentUser.user_id) {
      return
    }

    axios.get(routes.blockUserUrl, { params: { user_id: displayUser.user_id } }).then((response) => {
      setIsUserBlocked(response.data)
    }).catch(handleError)
  }, [displayUser])

  useEffect(() => {
    (async () => {
      if (!await authClient.configureMessaging()) {
        showAlert({ title: 'Notifications Disabled', message: 'Enable browser notifications to receive updated DMs.' })
      }
    })()
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    onMessage(getMessaging(), (payload) => {
      if (conversationId === payload?.data?.conversation_id) {
        refreshMessages()
      }
    })
  }, [])

  useEffect(() => {
    if (newestMessage) {
      updateMessageRead()
    }
  }, [newestMessage])

  useEffect(() => {
    const message = messages[0]
    if (message) {
      setNewestMessage(message)
    }
  }, [messages])

  const refreshMessages = () => {
    setLastRefreshTimestamp(Date.now())
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible' || !newestMessage) {
      return
    }

    axios.get(routes.dynamic(routes.conversationUrl, conversationId)).then((response) => {
      const conversation = response.data
      if (newestMessage.id !== conversation.newest_message?.id) {
        refreshMessages()
      }
    }).catch(handleError)
  }

  const calculateUpdatedMessages = (messages) => {
    let interval = dayjs.duration(15, 'minutes')
    for (let i = 0; i < messages.length; i++) {
      let prevMsg = i + 1 >= messages.length ? null : messages[i + 1]
      messages[i].showTimestamp = !prevMsg || prevMsg?.created_at && dayjs(messages[i].created_at).diff(dayjs(prevMsg.created_at), 'minutes') > interval.asMinutes()
      messages[i].showUserIcon = (messages[i].user_id !== prevMsg?.user_id || messages[i].showTimestamp) && messages[i].user_id !== currentUser.user_id
      messages[i].fromSelf = messages[i].user_id === currentUser.user_id
    }

    return messages
  }

  const handleSendMessage = (message) => {
    if (!/\S/.test(message)) {
      return
    }

    axios.post(routes.dynamic(routes.conversationMessageUrl, conversationId), {
      data: message
    }).then(() => {
      setNewMessage('')
      refreshMessages()
    }).catch(handleError)
  }

  if (loading) {
    return <Loading/>
  }

  const autoResize = () => {
    if (textareaContainerRef.current && textareaRef.current) {
      textareaContainerRef.current.style.height = 'auto'
      textareaContainerRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }

  const updateMessageRead = () => {
    if (lastReadMessage?.id === newestMessage?.id) {
      return
    }
    axios.put(
      routes.dynamic(routes.conversationMessageReadUrl, conversationId, newestMessage.id)
    ).then(() => {
      setLastReadMessage(newestMessage)
    }).catch(handleError)
  }

  const handleBlockUser = () => {
    if (isUserBlocked) {
      axios.delete(routes.blockUserUrl, {
        data: {
          user_id: displayUser.user_id,
        }
      }).then(() => {
        setIsUserBlocked(false)

        axios.get(routes.dynamic(routes.conversationUrl, conversationId)).then((response) => {
          const conversation = response.data
          setIsConversationBlocked(conversation.is_blocked)
        }).catch(handleError)
      }).catch(handleError)
    } else {
      axios.post(routes.blockUserUrl, {
        user_id: displayUser.user_id,
      }).then(() => {
        setIsUserBlocked(true)
        setIsConversationBlocked(true)
      }).catch(handleError)
    }
  }

  const handleArchiveConversation = () => {
    axios.put(routes.dynamic(routes.conversationArchiveUrl, conversationId)).then(() => {
      props.router.navigate(routes.producerConversationListPath)
    }).catch(handleError)
  }

  return <Layout>
    <div className='conversation_container'>
    <Layout.Header className='conversation_container_header'>
      <div className='conversation_container_header_spacer'></div>
      <a
        className='conversation_container_header_username'
        href={routes.dynamic(routes.applinksPublicUserProfileLink, displayUser?.user_id)}
        target='_blank'
        rel='noreferrer noopener'
      >
        <h5>{displayUser?.username}</h5>
      </a>
      <div className='conversation_container_header_actions'>
        <Cell.OverflowButton actions={[{title: isUserBlocked ? 'Unblock' : 'Block', onClick: () => showAlertConfirm({
          title: `${isUserBlocked ? 'Unblock' : 'Block'} ${displayUser.username}?`,
          message: isUserBlocked ? 'They will now be able to comment on and favorite your posts.' : 'They will be able to view your posts, but not comment on or favorite them.',
          callback: () => handleBlockUser()
        })},
        {
          title: 'Report',
          onClick: () => props.router.navigate(routes.dynamic(routes.producerReportPath, 'USER', displayUser.user_id))
        },
        {
          title: 'Archive',
          onClick: () => handleArchiveConversation()
        }
        ]} />
      </div>
    </Layout.Header>
    <div ref={listRef} className='conversation_messages-container'>
      <PaginatedList
        key={`messages-${lastRefreshTimestamp}`}
        loadUrl={routes.dynamic(routes.conversationMessageUrl, conversationId)}
        cell={MessageCell}
        cellItemPropName={'message'}
        itemsUpdated={(newMessages) => {
          setMessages(calculateUpdatedMessages(newMessages))
        }}
        cellAdditionalProps={{
          updatedMessages: messages,
          conversationMembers,
        }}
        reverse={true}
        onRender={() => {requestAnimationFrame(() => {
          if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight
          }
        })}}
        emptyStateOverride={<div className='messages-empty-state'>
          <img src={sadBoi} alt='sad boi' />
          <div>{`No conversation yet. Send a nice message to ${displayUser?.username}!`}</div>
        </div>}
      />
      </div>
    <div className='conversation_message_input-container'>
      <div className='conversation_message_input-bar' ref={textareaContainerRef}>
        <img src={currentUser.profile?.profile_pics['200']} className='conversation_message_profile-image'/>
        <textarea
          type='text'
          className='conversation_message_input'
          id='auto-resize-input'
          placeholder={isConversationBlocked ? 'Blocked' : 'New Message'}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSendMessage(newMessage)
            }
          }}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onInput={autoResize}
          ref={textareaRef}
          disabled={isConversationBlocked}
          />
          <Button
            className='conversation_message_send-button'
            skipDefaultClass={true}
            onClick={() => handleSendMessage(newMessage)}>
              <img
                src={sendMessage}
                alt='send'
                title='Send message'
                disabled={isConversationBlocked}/>
          </Button>
        </div>
      </div>
    </div>
  </Layout>
}

const MessageCell = ({message, updatedMessages, conversationMembers}) => {
  const messageToShow = updatedMessages.find((x) => x.id === message.id)
  if (!messageToShow) {
    return null
  }

  const displayTime =
    <div className='conversation_message_header'>
      <p className='conversation_message_header_paragraph'>
        <b>
          {dayjs(messageToShow.created_at).isSame(dayjs(), 'day')
            ? 'Today'
            : formattedDate(messageToShow.created_at)}
        </b>
        <span className='conversation_preview_time'>
          {formattedTime(messageToShow.created_at)}
        </span>
      </p>
    </div>

  const author = conversationMembers.find((x) => x.user_id === messageToShow.user_id)

  return <>
      {messageToShow.showTimestamp && displayTime}
      <Cell noline={true} cellClassName='conversation_message_cell'>
        {messageToShow.showUserIcon &&
          <a
            href={routes.dynamic(routes.applinksPublicUserProfileLink, author?.user_id)}
            target='_blank'
            rel='noreferrer noopener'
          >
          <Cell.Image
              src={author?.user_profile_images['200']}
              className='conversation_message_profile-image'
            />
          </a>
        }
        <Cell.Body>
          <span
            className={`conversation_message ${
              messageToShow.fromSelf
                ? 'conversation_message_self'
                : 'conversation_message_other'
            } ${
              !messageToShow.showUserIcon && !messageToShow.fromSelf
                ? 'conversation_message_no-icon'
                : ''
            }`}
          >
            {messageToShow.data}
          </span>
        </Cell.Body>
      </Cell>
    </>
}

export default withRouter(ConversationView)
